/**
 * Israel date/time helpers. "Today" and "now" on this site always mean Israel
 * time (Asia/Jerusalem), whatever time zone the phone or server is in.
 * Never use toISOString() for "today" — that gives the UTC date, which is
 * yesterday between midnight and ~2–3 AM in Israel.
 */

export const ISRAEL_TIME_ZONE = 'Asia/Jerusalem';

const formatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: ISRAEL_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** Israel date ('YYYY-MM-DD') and time ('HH:mm') at the given moment (default: now). */
export function getIsraelNow(at: Date = new Date()): { date: string; time: string } {
  const parts: Record<string, string> = {};
  for (const p of formatter.formatToParts(at)) parts[p.type] = p.value;
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    time: `${parts.hour}:${parts.minute}`,
  };
}

/** Today's date in Israel, 'YYYY-MM-DD'. */
export function getIsraelToday(at: Date = new Date()): string {
  return getIsraelNow(at).date;
}

/** Current time in Israel, 'HH:mm'. */
export function getIsraelTimeHHMM(at: Date = new Date()): string {
  return getIsraelNow(at).time;
}

/** A 'YYYY-MM-DD' date as a Date at noon UTC (safe for day arithmetic and Hebcal). */
export function dateStringToNoonUtc(date: string): Date {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12));
}

/** Add (or subtract) whole days to a 'YYYY-MM-DD' date. */
export function addDaysToDate(date: string, days: number): string {
  const d = dateStringToNoonUtc(date);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

const offsetFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: ISRAEL_TIME_ZONE,
  timeZoneName: 'shortOffset',
});

/**
 * True when Israel is on summer time (UTC+3) on this date, false on winter
 * time (UTC+2). Read from the time-zone database built into the browser, which
 * knows Israel's clock-change law, so it is right every year without dates.
 * Checked at noon, after the 02:00 switch.
 */
export function isIsraelSummerTime(date: string): boolean {
  const name = offsetFormatter.formatToParts(dateStringToNoonUtc(date)).find((p) => p.type === 'timeZoneName')?.value;
  return name === 'GMT+3';
}
